import { useState } from 'react';
import {
    Users,
    Trophy,
    Search,
    LineChart,
    Briefcase,
    UserCheck,
    Calendar,
    Star,
    CheckCircle,
    Clock,
    Building
} from 'lucide-react';
const Main = () => {
    const [selectedLead, setSelectedLead] = useState(null);

    // Mock historical success data
    const successPatterns = {
        enterprise: {
            "AI/ML": [
                {
                    team: ["Sarah Parker", "James Wilson"],
                    deals: 12,
                    value: "£2.4M",
                    avgSize: "£200k",
                    conversionRate: "75%",
                    specialties: ["Technical Sales", "IP Strategy"]
                }
            ],
            "Fintech": [
                {
                    team: ["Michael Chen", "Emma Roberts"],
                    deals: 8,
                    value: "£1.6M",
                    avgSize: "£200k",
                    conversionRate: "82%",
                    specialties: ["Regulatory Compliance", "Portfolio Strategy"]
                }
            ]
        }
    };

    // Mock team member profiles
    const teamProfiles = [
        {
            name: "Sarah Parker",
            role: "Senior IP Strategist",
            expertise: ["AI/ML", "Technical Sales"],
            recentDeals: 15,
            successRate: "92%",
            availability: "Next Week",
            strengths: [
                "Technical Understanding",
                "Enterprise Negotiations",
                "Portfolio Strategy"
            ],
            similarDeals: [
                {
                    company: "TechVision AI",
                    value: "£250k",
                    industry: "AI/ML",
                    timeframe: "3 months"
                }
            ]
        }
    ];

    // Mock current lead
    const currentLead = {
        company: "NextGen AI Solutions",
        industry: "AI/ML",
        size: "Enterprise",
        requirements: [
            "Technical expertise required",
            "Multi-territory filing strategy",
            "Portfolio consolidation needed",
            "Regulatory compliance focus"
        ],
        value: "£275k",
        complexity: "High",
        timeline: "3 months",
        similarTo: ["TechVision AI", "DataSphere Ltd"]
    };
    return (
        <div className="space-y-6">
            {/* Lead Requirements */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <Building className="w-5 h-5" />
                        <span className='font-bold'> Lead Profile</span>
                    </div>
                    <div>
                        Match requirements with team expertise
                    </div>
                </div>
                <div>
                    <div className="space-y-4 mt-2">
                        <div className="flex justify-between items-start">
                            <div>
                                <h3 className="font-medium">{currentLead.company}</h3>
                                <p className="text-sm text-gray-500">
                                    {currentLead.industry} • {currentLead.size}
                                </p>
                            </div>
                            <div className="text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-[#d1fae5] text-[#065f46]">
                                {currentLead.value}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mt-4">
                            <div>
                                <h4 className="text-sm font-medium mb-2">Requirements</h4>
                                <div className="space-y-2">
                                    {currentLead.requirements.map((req, index) => (
                                        <div key={index} className="flex items-start gap-2 text-sm">
                                            <div className="mt-1">
                                                <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
                                            </div>
                                            {req}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <h4 className="text-sm font-medium mb-2">Similar Deals</h4>
                                <div className="space-y-2">
                                    {currentLead.similarTo.map((company, index) => (
                                        <div key={index} className="text-sm flex items-center gap-2">
                                            <CheckCircle className="w-3 h-3 text-[#22c55e]" />
                                            {company}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Team Matching */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        <span className='font-bold'> Recommended Team</span>
                    </div>
                    <div>
                        Based on similar successful conversions
                    </div>
                </div>
                <div>
                    <div className="space-y-4 mt-2">
                        {teamProfiles.map((profile, index) => (
                            <div key={index} className="border rounded-lg p-4">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="font-medium">{profile.name}</h3>
                                        <p className="text-sm text-gray-500">{profile.role}</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <div className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800'>
                                            {profile.successRate} Success Rate
                                        </div>
                                        <div className="text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-[#d1fae5] text-[#065f46]">
                                            {profile.recentDeals} Recent Deals
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <h4 className="text-sm font-medium mb-2">Expertise Match</h4>
                                        <div className="space-y-2">
                                            {profile.expertise.map((skill, idx) => (
                                                <div key={idx} className="flex items-center gap-2">
                                                    <Star className="w-3 h-3 text-[#eab308]" />
                                                    <span className="text-sm">{skill}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium mb-2">Key Strengths</h4>
                                        <div className="space-y-2">
                                            {profile.strengths.map((strength, idx) => (
                                                <div key={idx} className="text-sm flex items-center gap-2">
                                                    <CheckCircle className="w-3 h-3 text-[#22c55e]" />
                                                    {strength}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="border-t pt-4">
                                    <h4 className="text-sm font-medium mb-2">Similar Deals Closed</h4>
                                    <div className="space-y-2">
                                        {profile.similarDeals.map((deal, idx) => (
                                            <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                                <div>
                                                    <div className="text-sm font-medium">{deal.company}</div>
                                                    <div className="text-xs text-gray-500">
                                                        {deal.industry} • {deal.timeframe}
                                                    </div>
                                                </div>
                                                <div className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800'>{deal.value}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex justify-between items-center mt-4 pt-4 border-t">
                                    <div className="text-sm text-gray-500">
                                        Available: {profile.availability}
                                    </div>
                                    <div className="flex gap-2">
                                        <button className="flex items-center gap-2 px-4 py-2 rounded-lg border border-green text-green">
                                            View Schedule
                                        </button>
                                        <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                            Request for Pitch
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Success Patterns */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <Trophy className="w-5 h-5" />
                        <span className='font-bold'>Success Patterns</span>
                    </div>
                    <div>
                        Historical team performance in similar deals
                    </div>
                </div>
                <div>
                    <div className="space-y-4 mt-2">
                        {successPatterns.enterprise["AI/ML"].map((pattern, index) => (
                            <div key={index} className="border rounded-lg p-4">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <div className="font-medium">{pattern.team.join(" + ")}</div>
                                        <div className="text-sm text-gray-500">
                                            {pattern.deals} deals • {pattern.value} total value
                                        </div>
                                    </div>
                                    <div className="text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-[#d1fae5] text-[#065f46]">
                                        {pattern.conversionRate} Success
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <div className="text-sm text-gray-500 mb-1">Average Deal Size</div>
                                        <div className="font-medium">{pattern.avgSize}</div>
                                    </div>
                                    <div>
                                        <div className="text-sm text-gray-500 mb-1">Specialties</div>
                                        <div className="flex gap-2">
                                            {pattern.specialties.map((specialty, idx) => (
                                                <div key={idx} className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800'>
                                                    {specialty}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
export default Main